import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';

const API = process.env.REACT_APP_API_URL;

const Homepage = () => {
  // ── Titles (categories) ──
  const [titles, setTitles] = useState([]);
  const [titleName, setTitleName] = useState('');
  const [creating, setCreating] = useState(false);

  // ── Upload ──
  const [selectedTitleId, setSelectedTitleId] = useState('');
  const [images, setImages] = useState([]);
  const [captions, setCaptions] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);

  // ── Uploaded images ──
  const [uploadedImages, setUploadedImages] = useState([]);

  useEffect(() => {
    fetchTitles();
    fetchUploadedImages();
  }, []);

  const fetchTitles = async () => {
    try {
      const res = await fetch(`${API}/overview-titles`);
      if (res.ok) setTitles(await res.json());
    } catch (error) {
      console.error('Error fetching overview titles:', error);
    }
  };

  const fetchUploadedImages = async () => {
    try {
      const res = await fetch(`${API}/overview-upload`);
      if (res.ok) setUploadedImages(await res.json());
    } catch (error) {
      console.error('Error fetching overview images:', error);
    }
  };

  // ── Title handlers ──
  const handleCreateTitle = async () => {
    if (!titleName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/overview-titles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: titleName.trim() }),
      });
      if (res.ok) {
        setTitleName('');
        fetchTitles();
      } else {
        console.error('Error creating overview title');
      }
    } catch (error) {
      console.error('Error creating overview title:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTitle = async (id) => {
    try {
      const res = await fetch(`${API}/overview-titles/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setTitles((prev) => prev.filter((t) => t._id !== id));
        if (selectedTitleId === id) setSelectedTitleId('');
      }
    } catch (error) {
      console.error('Error deleting overview title:', error);
    }
  };

  // ── Upload handlers ──
  const handleImageChange = (event) => {
    previews.forEach((url) => URL.revokeObjectURL(url));
    const selected = Array.from(event.target.files);
    setImages(selected);
    setCaptions(selected.map(() => ''));
    setPreviews(selected.map((file) => URL.createObjectURL(file)));
  };

  const handleCaptionChange = (index, value) => {
    setCaptions((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const resetSelection = () => {
    previews.forEach((url) => URL.revokeObjectURL(url));
    setImages([]);
    setCaptions([]);
    setPreviews([]);
  };

  const handleUpload = async () => {
    if (!selectedTitleId) {
      alert('Please select an overview category');
      return;
    }
    if (images.length === 0) {
      alert('Please select at least one image to upload');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      images.forEach((image, index) => {
        formData.append('images', image);
        formData.append('captions', captions[index] || '');
      });

      const res = await fetch(`${API}/overview-upload/${selectedTitleId}`, {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        alert('Images uploaded successfully');
        resetSelection();
        fetchUploadedImages();
      } else {
        console.error('Error uploading images');
      }
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (id) => {
    try {
      const res = await fetch(`${API}/overview-upload/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setUploadedImages((prev) => prev.filter((img) => img.id !== id));
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50" style={{ fontFamily: "Muli" }}>
      <Sidebar />
      <div className="w-full">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white/80 backdrop-blur px-6 py-4">
          <p className="text-sm font-medium text-gray-400">Admin Dashboard</p>
          <span className="text-sm font-semibold text-gray-700">Welcome, Admin</span>
        </div>

        <div className="mx-auto max-w-5xl px-6 py-8">
          {/* Page heading */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Overview</h1>
            <p className="mt-1 text-sm text-gray-500">
              Create overview categories (e.g. Personal Work, Celebrity, Ads/Tear Sheet) and upload images to each one.
            </p>
          </div>

          {/* ── Create category ── */}
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <label className="block text-sm font-semibold text-gray-700 mb-2">New overview category</label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                type="text"
                placeholder="e.g. Personal Work"
                value={titleName}
                onChange={(e) => setTitleName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateTitle()}
              />
              <button
                className="inline-flex items-center justify-center rounded-xl bg-gray-900 px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleCreateTitle}
                disabled={creating || !titleName.trim()}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>

            {/* Category list */}
            {titles.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {titles.map((t) => (
                  <span
                    key={t._id}
                    className="inline-flex items-center gap-2 rounded-full bg-gray-100 py-1.5 pl-4 pr-2 text-sm text-gray-700"
                  >
                    {t.name}
                    <button
                      onClick={() => handleDeleteTitle(t._id)}
                      className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500 hover:text-white"
                      title="Delete category"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* ── Upload images to a category ── */}
          <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-bold text-gray-900">Upload Images</h2>

            <label className="mb-2 block text-sm font-semibold text-gray-700">Category</label>
            <select
              className="w-full sm:w-80 cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
              value={selectedTitleId}
              onChange={(e) => setSelectedTitleId(e.target.value)}
            >
              <option value="">Select a category</option>
              {titles.map((t) => (
                <option key={t._id} value={t._id}>{t.name}</option>
              ))}
            </select>

            {/* Dropzone */}
            <label
              htmlFor="overview-files"
              className="mt-5 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center transition hover:border-gray-900 hover:bg-gray-100"
            >
              <svg className="mb-3 h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <span className="text-sm font-semibold text-gray-700">Click to choose images</span>
              <span className="mt-1 text-xs text-gray-400">You can select multiple files (up to 10)</span>
              <input
                id="overview-files"
                type="file"
                onChange={handleImageChange}
                className="hidden"
                multiple
                accept="image/*"
              />
            </label>

            {/* Selected files with optional captions */}
            {images.length > 0 && (
              <div className="mt-6">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Selected images <span className="text-gray-400">({images.length})</span>
                  </h3>
                  <button type="button" onClick={resetSelection} className="text-xs font-medium text-gray-400 hover:text-red-500">
                    Clear all
                  </button>
                </div>
                <div className="space-y-3">
                  {images.map((image, index) => (
                    <div key={index} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <img src={previews[index]} alt={image.name} className="h-16 w-16 flex-shrink-0 rounded-lg object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="mb-2 truncate text-xs font-medium text-gray-500">{image.name}</p>
                        <input
                          type="text"
                          placeholder="Caption (optional)"
                          value={captions[index] || ''}
                          onChange={(e) => handleCaptionChange(index, e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-gray-900 px-8 py-3.5 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Images'}
            </button>
          </div>

          {/* ── Uploaded images table ── */}
          <div className="mt-10">
            <h2 className="mb-4 text-xl font-bold text-gray-900">Uploaded Images</h2>
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">S/N</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Image</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Caption</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {uploadedImages.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-400">No images uploaded yet.</td>
                      </tr>
                    ) : (
                      uploadedImages.map((image, index) => (
                        <tr className="transition hover:bg-gray-50" key={image.id || index}>
                          <td className="px-6 py-4 text-sm text-gray-500">{index + 1}</td>
                          <td className="px-6 py-4 text-sm font-medium capitalize text-gray-900">{image.titleName}</td>
                          <td className="px-6 py-4">
                            <img src={image.image} alt="" className="h-14 w-14 rounded-lg object-cover ring-1 ring-gray-200" />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {image.caption || <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              className="rounded-lg bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-500 hover:text-white"
                              onClick={() => handleDeleteImage(image.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Homepage;
